import { useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ShareIcon from "@mui/icons-material/Share";
import StandardDialog from "../../components/StandardDialog";
import { useAuth } from "../../context/authContext";
import { useThemeContext } from "../../context/themeContext";
import { SHARE_ORIGIN } from "../../utils/detailPaths";
import { shareLink } from "../../utils/share";
import UserSearch from "../explore/UserSearch";

/**
 * Opened from Getting started's "Follow 3 people" step: nudges you to share
 * your profile so friends join and you can follow each other, or to find
 * people already here (the app's profile search, as on Explore).
 */
export default function ShareProfileDialog({ open, onClose }) {
  const { user } = useAuth();
  const { isTouchDevice } = useThemeContext();
  // Lives here rather than with the caller, so it stays open after this
  // dialog closes to make way for it
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleShare = () => {
    shareLink({
      url: `${SHARE_ORIGIN}/u/${user.username}`,
      title: `@${user.username} on Curate`,
      copiedMessage: "Link copied! Paste it anywhere to share your profile",
    });
  };

  const handleSearchProfiles = () => {
    onClose();
    setIsSearchOpen(true);
  };

  return (
    <>
      <StandardDialog open={open} onClose={onClose} maxWidth="xs">
        <DialogTitle
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Curate with friends
          {/* 44px target pulled into the padding, as in the achievement details */}
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              width: 44,
              height: 44,
              my: -1.5,
              mr: -1.5,
              color: "text.secondary",
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Share your profile with friends so they can follow you, then follow
            them back to see what they're watching and reading. Follow 3 people
            to complete this step.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={handleSearchProfiles}>
            Search profiles
          </Button>
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={handleShare}
          >
            Share profile
          </Button>
        </DialogActions>
      </StandardDialog>

      {/* Profile search, as opened from Explore's "Following" empty state */}
      <StandardDialog
        open={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        maxWidth="sm"
        topOffset="10vh"
        disableRestoreFocus
      >
        <DialogContent
          sx={{ border: "none", display: "flex", flexDirection: "column" }}
        >
          <UserSearch
            fullscreen={isTouchDevice}
            onClose={() => setIsSearchOpen(false)}
          />
        </DialogContent>
      </StandardDialog>
    </>
  );
}
